<template>
  <DropdownHybrid
    ref="dropdown"
    v-model="dropdownOpen"
    :sheet-title="t('page.bookmarks.sorting.sheet')"
    :menu-props="{left: true, minWidth: 240}"
  >
    <template #activator="{ props: menuProps }">
      <v-tooltip
        :open-on-hover="true"
        location="top" :open-delay="300"
        content-class="text-neutral0 bg-neutral900"
        :disabled="isMobile || dropdownOpen"
      >
        <span>{{ t('cta.sort') }}</span>
        <template #activator="{ props: tooltipProps }">
          <BrandButton
            variant="secondary"
            color="neutral25 text-neutral900"
            :class="$attrs.class"
            v-bind="{ ...tooltipProps, ...menuProps, ...toolbarBtnProps }"
            flat
          >
            <component :is="currentOption.icon" class="text-h5" />
            <span v-if="!isCompact" class="ml-3">{{ t(currentOption.string) }}</span>
          </BrandButton>
        </template>
      </v-tooltip>
    </template>

    <v-list bg-color="neutral25" class="py-3">
      <v-list-item v-for="option in options" :key="option.key" @click="onSelect(option.value)">
        <template #prepend>
          <component :is="option.icon" class="text-h5 mr-3" />
        </template>
        <v-list-item-title class="text-neutral900 font-weight-medium">
          {{ t(option.text) }}
        </v-list-item-title>
      </v-list-item>
    </v-list>
  </DropdownHybrid>
</template>

<script setup>
import { ref, computed, defineProps, defineEmits } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import { useDisplay } from 'vuetify'
import { BOOKMARK_SORT_FIELDS, BOOKMARK_SORT_OPTIONS_UI } from '@/utils/constants'
import { useToolbarButtonStyle } from '@/composables/useToolbarButtonStyle'

const props = defineProps({
  sorting: {
    type: Array,
    required: true,
  },
})

const emit = defineEmits(['set'])

// Composables
const { t } = useI18n()
const store = useStore()
const display = useDisplay()
const isMobile = computed(() => display.smAndDown.value)
const { isCompact, toolbarBtnProps } = useToolbarButtonStyle()
const dropdown = ref(null)
const dropdownOpen = ref(false)

// Computed properties
const isCollection = computed(() => store.getters['collectionItems/isCollection'])

const currentOption = computed(() => {
  const direction = props.sorting[1] ? 'asc' : 'desc'
  const field = props.sorting[0]
  const key = `${field}-${direction}`
  return BOOKMARK_SORT_OPTIONS_UI[key]
})

const options = computed(() => {
  const fields = [
    {
      field: BOOKMARK_SORT_FIELDS.createdAt,
      asc: false,
    },
    {
      field: BOOKMARK_SORT_FIELDS.createdAt,
      asc: true,
    },
    {
      field: BOOKMARK_SORT_FIELDS.title,
      asc: true,
    },
    {
      field: BOOKMARK_SORT_FIELDS.title,
      asc: false,
    },
    {
      field: BOOKMARK_SORT_FIELDS.domain,
      asc: true,
    },
    {
      field: BOOKMARK_SORT_FIELDS.domain,
      asc: false,
    },
    {
      field: BOOKMARK_SORT_FIELDS.commentsNumber,
      asc: false,
    },
    {
      field: BOOKMARK_SORT_FIELDS.commentsNumber,
      asc: true,
    },
    {
      field: BOOKMARK_SORT_FIELDS.commentsDate,
      asc: false,
    },
    {
      field: BOOKMARK_SORT_FIELDS.commentsDate,
      asc: true,
    },
  ]

  if (isCollection.value) {
    fields.push(
      {
        field: BOOKMARK_SORT_FIELDS.likes,
        asc: false,
      },
      {
        field: BOOKMARK_SORT_FIELDS.likes,
        asc: true,
      },
    )
  }

  const options = fields.map(({ field, asc }) => {
    const direction = asc ? 'asc' : 'desc'
    const key = `${field}-${direction}`

    return {
      key,
      text: BOOKMARK_SORT_OPTIONS_UI[key].string,
      icon: BOOKMARK_SORT_OPTIONS_UI[key].icon,
      value: [field, asc],
    }
  })

  return options
})

// Methods
const onSelect = val => {
  dropdown.value?.close()
  emit('set', val)
}
</script>
